import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star as StarIcon, ArrowRight, Scale, ListChecks, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function StarsList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useSeo({
        title: "Yıldız Standartları",
        description: "Türkiye Turizm Bakanlığı yönetmeliğine göre 1-5 yıldız otel standartları, kategori bazlı zorunlu kriterler ve karşılaştırma araçları.",
        path: "/yildiz-standartlari",
    });

    useEffect(() => {
        api.get("/stars")
            .then(({ data }) => setItems(data.items || []))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stars-list-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-14 lg:py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">Bakanlık Standartları</span>
                    <h1 className="mt-3 text-4xl sm:text-6xl font-bold tracking-tight inline-flex items-center gap-3">
                        <StarIcon className="w-10 h-10 text-gold" /> Yıldız Standartları
                    </h1>
                    <p className="mt-4 text-white/75 max-w-2xl leading-relaxed">
                        Türkiye Turizm Bakanlığı'nın yayınladığı "Turizm Tesislerinin Niteliklerine İlişkin Yönetmelik" çerçevesinde 1-5 yıldız oteller için zorunlu kriterler — kategori bazında detaylı veritabanı.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                            to="/yildiz-standartlari/karsilastir"
                            className="inline-flex items-center gap-2 bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-5 py-3 rounded-md shadow-lg"
                            data-testid="stars-compare-cta"
                        >
                            <Scale className="w-4 h-4" /> Karşılaştırma Tablosu
                        </Link>
                        <Link
                            to="/yildiz-standartlari/kontrol"
                            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-5 py-3 rounded-md shadow-lg"
                            data-testid="stars-check-cta"
                        >
                            <ListChecks className="w-4 h-4" /> Kaç Yıldızı Hak Ediyorsunuz?
                        </Link>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-14">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Yükleniyor...</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stars-grid">
                        {items.map((s) => (
                            <Link
                                key={s.star_level}
                                to={`/yildiz-standartlari/${s.star_level}`}
                                data-testid={`stars-card-${s.star_level}`}
                                className="lift-card group bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-200 hover:border-gold/40 p-5 text-center"
                            >
                                <div className="font-data text-3xl font-extrabold text-gold tracking-tight">
                                    {Array.from({ length: s.star_level }).map(() => "★").join("")}
                                </div>
                                <h3 className="mt-2 text-base font-bold text-dark-navy">{s.star_level} Yıldız</h3>
                                <p className="mt-1 text-xs text-deep-blue/70">{s.total_requirements} kriter · {s.mandatory_count} zorunlu</p>
                                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 group-hover:text-gold-700">
                                    İncele <ArrowRight className="w-3 h-3" />
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            <section className="bg-white py-14 border-t border-slate-200">
                <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-5">
                    <InfoCard Icon={ShieldCheck} title="Resmi Kaynak" desc="Tüm kriterler T.C. Kültür ve Turizm Bakanlığı'nın yayınladığı yönetmelik çerçevesinde derlenmiştir." />
                    <InfoCard Icon={Scale} title="Karşılaştırma" desc="5 farklı yıldız seviyesini kategori bazında karşılaştırın, yatırım kararınızı netleştirin." />
                    <InfoCard Icon={ListChecks} title="Kontrol Listesi" desc="Mevcut tesisinizin hangi yıldız seviyesini hak ettiğini interaktif soru-cevap ile öğrenin." />
                </div>
            </section>
        </div>
    );
}

function InfoCard({ Icon, title, desc }) {
    return (
        <div className="rounded-lg border border-slate-100 p-5 bg-light-bg">
            <span className="inline-flex w-10 h-10 rounded-md bg-teal/10 text-teal-700 items-center justify-center">
                <Icon className="w-5 h-5" />
            </span>
            <h3 className="mt-3 text-base font-bold text-dark-navy">{title}</h3>
            <p className="mt-1 text-sm text-deep-blue/80">{desc}</p>
        </div>
    );
}
